import { ChevronLeft, ChevronRight } from 'lucide-react';

/* =====================================================================
   Pagination (guía v2 § 10.7 / § 2.11)
   - page/limit (no per_page)
   - allowed limits: 10 / 25 / 50
   - Numbered buttons con ellipsis cuando hay más de 7 páginas
===================================================================== */

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (limit: number) => void;
  itemName?: string;
  allowedLimits?: number[];
}

const DEFAULT_LIMITS = [10, 25, 50];

export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemName = 'items',
  allowedLimits = DEFAULT_LIMITS,
}: PaginationProps) {
  if (totalItems === 0) return null;

  const from = (currentPage - 1) * itemsPerPage + 1;
  const to = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="pagination">
      <div className="pagination-info">
        <span className="pagination-range">
          {from}–{to} de <b>{totalItems}</b> {itemName}
        </span>
        {onItemsPerPageChange && (
          <label className="pagination-limit">
            Por página:
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            >
              {allowedLimits.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="pagination-pages">
        <button
          type="button"
          className="pagination-btn"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Página anterior"
        >
          <ChevronLeft className="w-[14px] h-[14px]" />
        </button>

        {getPageList(currentPage, totalPages).map((p, i) =>
          p === '…' ? (
            <span key={`gap-${i}`} className="pagination-gap">…</span>
          ) : (
            <button
              key={p}
              type="button"
              className={`pagination-btn ${p === currentPage ? 'active' : ''}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? 'page' : undefined}
            >
              {p}
            </button>
          ),
        )}

        <button
          type="button"
          className="pagination-btn"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Página siguiente"
        >
          <ChevronRight className="w-[14px] h-[14px]" />
        </button>
      </div>
    </div>
  );
}

/** Compacta la lista de páginas con `…` cuando hay muchas. */
function getPageList(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '…')[] = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  if (left > 2) pages.push('…');
  for (let p = left; p <= right; p++) pages.push(p);
  if (right < total - 1) pages.push('…');
  pages.push(total);
  return pages;
}
